import {gql} from '@apollo/client';
import React, {useState} from 'react';
import {debounce} from 'ts-debounce';
import _ from 'underscore';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import StorableSummaryExtrasManager from 'models/managers/StorableSummaryExtrasManager';
import {getExtraFromResponse} from 'samples/storables/StorableSearchInput';
import StorableStore from 'samples/StorableStore';
import {
  SearchInputQuery,
  // eslint-disable-next-line camelcase
  SearchInputQuery_searchableItems,
  // eslint-disable-next-line camelcase
  SearchInputQuery_searchableItems_InaccessibleInfoNode,
  SearchInputQueryVariables,
} from 'search/inputs/__generated/SearchInputQuery';
import {
  DROPDOWN_SEARCH_URL,
  DropdownSearchResults,
  Filter,
  FilterKey,
  keyToEmptyFilter,
  SearchContext,
  Sort,
  SortKey,
} from 'search/SearchConstants';
import BenchChip, {BenchChipProps} from 'ui-library/chips/BenchChip';
import Chip, {ChipProps, ChipSize} from 'ui-library/chips/Chip';
import ChipSearchInput from 'ui-library/inputs/ChipSearchInput';
import {ChipSelectInputProps} from 'ui-library/inputs/ChipSelectInput';
import ItemOptionContent, {getIconIdForSearchResult} from 'ui-library/inputs/ItemOptionContent';
import {OptionData, OptionValue} from 'ui-library/menus/MenuConstants';
import OptionContent from 'ui-library/menus/OptionContent';
import APIUtil from 'util/APIUtil';
import {postJSONPromise} from 'util/Net';

export const NULL_LABEL = 'None';

export interface SearchInputProps
  // Coupling to ChipSelectInputProps because this should support any of the basic input props for that
  // component, and we want to make sure to capture them as that component eveolves to avoid feature
  // drift.
  extends Omit<
    ChipSelectInputProps,
    | 'chipClass'
    | 'getChipProps'
    | 'onQueryChange'
    | 'onRemove'
    | 'onSelect'
    | 'optionDatas'
    | 'query'
    | 'value'
    | 'renderOptionContent'
    | 'renderChips'
  > {
  additionalFilters?: Array<Filter>;
  hideSelectedResults?: boolean;
  onRemove: (itemId: string | null) => void;
  onSelect: (itemId: string | null) => void;
  preserveQueryOnSelect?: boolean;
  schemaIds?: Array<string>;
  schemaInterfaceIds?: Array<string>;
  selected: Array<string | null>;
  sorts?: Array<Sort>;
  types: Array<string>;
  includeNull?: boolean;
}

function getOptionDatasFromResponse(resp: SearchInputResponse): Array<OptionData> {
  const itemsById = _.indexBy(resp.items, 'id');
  // The doPostSearch below already fetched the up to date data for the returned storable ids
  const extras = StorableStore.getStorableSummaryExtras();
  const extraResp = {storableSummaryExtras: extras};

  return resp.ids
    .filter((id) => Boolean(itemsById[id]))
    .map((id) => {
      const item = itemsById[id];
      const value = {
        item,
        extra: APIUtil.isStorable(id) ? getExtraFromResponse(extraResp, id) : undefined,
        id: item.id,
        name: item.name,
        type: getModelType(item),
      };
      return {
        name: item.name,
        icon: getIconIdForSearchResult(value),
        value,
      };
    });
}

function getModelType(item: DropdownSearchItem): ModelTypeConstants {
  const type = APIUtil.apiIdToType(item.id);
  if (type === ModelTypeConstants.ENTITY) {
    if (item.__typename === 'OrganizationNode') {
      return ModelTypeConstants.ORGANIZATION;
    } else if (item.__typename === 'UserNode') {
      return ModelTypeConstants.USER;
    }
  }

  return type;
}

const DEBOUNCE_MS = 250;
// Writing own version of ChipSearchInput's searchPost function using promises instead
// The search api call only returns api ids, and then we make a GQL call to get the necessary data info to display.
// And if it's there are storables, we also fetch the storable extras.
async function doPostSearch(url: string, query: string, params: object): Promise<SearchInputResponse> {
  const apiIdOnlySearchData: DropdownSearchResults = await postJSONPromise({url, data: {query, ...params}});

  const storableIds = apiIdOnlySearchData.ids.filter((apiId: string) => APIUtil.isStorable(apiId));
  const [queryResults, _] = await Promise.all([
    doQuerySafe<SearchInputQuery, SearchInputQueryVariables>(
      {query: SEARCH_INPUT_QUERY},
      {fetchPolicy: 'network-only', variables: {searchIds: apiIdOnlySearchData.ids}}
    ),
    storableIds.length > 0 ? StorableSummaryExtrasManager.fetchMulti(storableIds) : Promise.resolve(),
  ]);

  return {
    ids: apiIdOnlySearchData.ids,
    items: queryResultsToItems(queryResults),
    correlationId: apiIdOnlySearchData.correlationId,
  };
}
// Held in an object so tests can stub it out.
export const dropdownSearch = {
  searchPostPromise: debounce(doPostSearch, DEBOUNCE_MS),
};

export const SEARCH_INPUT_QUERY = gql`
  query SearchInputQuery($searchIds: [StrictString!]!) @skipAudit @engComponent(name: SIGHTGLASS) {
    searchableItems(ids: $searchIds) {
      ... on SearchableInterface {
        id
        name
      }
      ... on HasCreatorInterface {
        creator {
          id
          name
        }
      }
      ... on HasArchiveRecordInterface {
        ...HasArchiveRecordGraphQL
      }
      ... on CoreObjectArchivableInterface {
        id
        archiveData {
          archived
          purpose
        }
      }
      ... on PartyInterface {
        id
        handle
        avatarUrl
      }
      ... on BaseFolderItemInterface {
        id
        folder {
          id
          source {
            id
            name
          }
        }
      }

      ... on FolderItemInterface {
        id
        folder {
          id
          source {
            id
            name
          }
        }
      }

      ... on RegistryItemInterface {
        id
        folder {
          id
          source {
            id
            name
          }
        }
        bioentityAliasInfos {
          id
          alias
          registryCollectionOwnerJson
        }
      }

      ... on StorableInterface {
        id
        barcode {
          value
        }
        storableSchema {
          id
          ... on PlateSchemaNode {
            id
            plateSchemaType
            height
            width
          }
          ... on BoxSchemaNode {
            id
            height
            width
          }
        }
      }

      ... on TemplateCollectibleInterface {
        id
        creator {
          id
          name
        }
      }

      ... on AnalysisNode {
        id
        project {
          id
          name
        }
      }

      ... on AnalyticsDashboardNode {
        id
        project {
          id
          name
        }
      }

      ... on ProcessOutputNode {
        id
        task {
          id
          humanId
        }
      }

      ... on ProcessTaskNode {
        id
        executionType
        assignee {
          ... on UserNode {
            id
            handle
            name
            avatar_url: avatarUrl
          }
        }
        schema {
          id
          name
        }
      }

      ... on TestDefinitionNode {
        id
        description
        resultSchemaCorrect {
          ... on ResultSchemaNode {
            id
            name
          }
        }
      }

      ... on WorkflowNode {
        id
        source {
          id
          name
        }
      }

      ... on WorklistNode {
        id
        itemType
        modifiedAt
      }

      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('HasArchiveRecord').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface SearchInputResponse {
  items: Array<DropdownSearchItem>;
  ids: Array<string>;
  correlationId: string;
}

export type DropdownSearchItem =
  // eslint-disable-next-line camelcase
  Exclude<SearchInputQuery_searchableItems, SearchInputQuery_searchableItems_InaccessibleInfoNode>;

function queryResultsToItems(queryResults: SearchInputQuery): Array<DropdownSearchItem> {
  return [...accessibleObjs(queryResults.searchableItems)];
}

function SearchInputChip(props: ChipProps | BenchChipProps): JSX.Element {
  if (!props.hasOwnProperty('id')) {
    return <Chip {...(props as ChipProps)} />;
  } else {
    return <BenchChip {...(props as BenchChipProps)} />;
  }
}

/**
 * Renders an input for searching for specified item types by name.
 *
 * Built on top of ChipSelectInput, so supports displaying selected items as chips in the input - although
 * this can be disabled.
 *
 * The goal of this component is to provide a single, unified search input that can handle all linkable
 * items, as well as additional constraints, such as schemas, schema interfaces, and any additional filters
 * that should be applied.
 *
 * To allow more flexible usage in contexts where full items have not been loaded for selected values, the
 * interface operates solely on ids.
 *
 * NOTE:
 * Several of the parameters exist to capture existing inconsistencies within the UI so that we can replace
 * all existing dropdown search components with a single implementation. The long term goal is to consolidate
 * behavior and reduce the number of configuration points. Introducing new inconsistencies should be avoided
 * as much as possible - prefer evaluating propsoed changes across the whole product and favoring universal
 * behavior updates over new configuration points. Ideally, only settings capturing differences in the nature
 * of the data being input, such as isMulti, would be a configurable.
 *
 * @param props - Component props; supports most features of ChipSelectInputProps that are not explicitly
 *                related to rendering options or selected chips, specifying query input, or selecting
 *                results. Only props unique to this component will be covered explicitly below.
 * @param {Array[Filter]} props.[additionalFilters] - Any non-standard filters to apply to the search request.
 *      TYPES, SCHEMA, and SCHEMA_INTERFACE filters should be specified via their dedicate convenience props
 *      instead.
 * @param {boolean} props.[hideSelectedResults] - When true, selected results will be filtered out of the
 *      results list. Has no effect if isMulti is false, since searches will not run if a value is already
 *      selected for single value inputs.
 * @param {function} props.onRemove - Event handler for removing a selected item; passed the item id.
 * @param {function} props.onSelect - Event handler for selecting an item; passed the item id.
 * @param {boolean} props.[preserveQueryOnSelect] - When true, the query text will not be cleared after
 *      selecting an item. Primarily intended to be used with isMulti.
 *      NOTE: This is one of the inconsistent behaviors that will ideally be consolidated. Prefer false for
 *      now, as that is most common.
 * @param {Array<string>} props.[schemaIds] - List of ids of a schemas to restrict results to. Cannot be used
 *      alongside schemaInterfaceIds.
 * @param {Array[string]} props.[schemaInterfaceIds] - Id of a schema interface to restrict restults to.
 *      Cannot be used alongside schemaId.
 * @param {Array[Sort]} props.[sorts] - Sorts to apply to the search results. If not specified, results will use
 *    the default relevance sort.
 * @param {Array[string]} props.selected - The ids of selected items.
 * @param {Array[string]} props.types - The types to search for. This can be any type supported by search as
 *      allowed in a FilterKey.TYPES filter. Genearally these values will come from ModelTypeConstants.
 */
export default function SearchInput({
  additionalFilters,
  hideSelectedResults,
  onRemove,
  onSelect,
  preserveQueryOnSelect,
  schemaIds,
  schemaInterfaceIds,
  selected,
  sorts,
  types,
  ...props
}: SearchInputProps): JSX.Element {
  if (types.length === 0) {
    throw new Error('At least one type must be specified.');
  }

  if (schemaIds?.length && schemaInterfaceIds?.length) {
    throw new Error('Only one of schemaId or schemaInterfaceIds is supported.');
  }

  const filters = [...(additionalFilters ?? [])];

  if (schemaIds?.length) {
    filters.push({
      ...keyToEmptyFilter(FilterKey.SCHEMA),
      value: schemaIds,
    });
  }

  if (schemaInterfaceIds?.length) {
    filters.push({
      ...keyToEmptyFilter(FilterKey.SCHEMA_INTERFACE),
      value: schemaInterfaceIds,
    });
  }

  const [query, setQuery] = useState('');
  const fetchParams = {
    offset: 0,
    limit: 25,
    sorts: sorts ?? [{key: SortKey.SORT_RELEVANCE_KEY, reverse: false}],
    filters: [
      {
        ...keyToEmptyFilter(FilterKey.TYPES),
        value: types,
      },
      ...filters,
    ],
    context: SearchContext.LISTING,
  };

  const doSearchPromise = (): Promise<Promise<SearchInputResponse>> =>
    dropdownSearch.searchPostPromise(DROPDOWN_SEARCH_URL, query, fetchParams);

  return (
    <ChipSearchInput<SearchInputResponse>
      {...props}
      // TODO(Search): This is not ideal right now becaues this endpoint does not restrict to name searches.
      // However, it supports taking the full set of search params, which none of the dropdown search
      // endpoints do, which we need. Implement a dedicated dropdown search endpoint and replace this to
      // get the ideal behavior.
      // In the mean time, this should be acceptable since content and tag matches are always ranked lower
      // than name matches, so this shouldn't majorly impact the very limited number of places this is used
      // to date.
      fetchParams={fetchParams}
      doSearchPromise={doSearchPromise}
      chipClass={SearchInputChip}
      getChipProps={(optionValue) => {
        if (optionValue.id == null) {
          return {
            text: NULL_LABEL,
            size: ChipSize.MINI,
          };
        } else {
          return {
            id: optionValue.id,
            isLink: false,
            size: ChipSize.MINI,
          };
        }
      }}
      getOptionDatasFromResponse={(resp: SearchInputResponse) => {
        const optionDatas = getOptionDatasFromResponse(resp).filter(
          (optionData: OptionData) =>
            !hideSelectedResults || !selected.find((selectedId) => selectedId === optionData.value.id)
        );
        if (props.includeNull && (!hideSelectedResults || !selected.includes(null))) {
          optionDatas.unshift({
            name: NULL_LABEL,
            value: null,
          });
        }
        return optionDatas;
      }}
      getCorrelationId={(resp: SearchInputResponse) => resp.correlationId}
      onQueryChange={(e) => {
        setQuery(e.target.value);
      }}
      onRemove={(value: OptionValue) => {
        onRemove(value != null ? value.id : value);
      }}
      onSelect={(value: OptionValue) => {
        const valueId = value != null ? value.id : value;
        if (selected.includes(valueId)) {
          onRemove(valueId);
        } else {
          if (!preserveQueryOnSelect) {
            setQuery('');
          }
          onSelect(valueId);
        }
      }}
      onBlur={() => {
        setQuery('');
      }}
      query={query}
      renderOptionContent={(optionData) =>
        optionData.value == null ? (
          <OptionContent>{optionData.name}</OptionContent>
        ) : (
          <ItemOptionContent optionData={optionData} />
        )
      }
      searchOnEmptyQuery={false}
      value={selected.map((id) => ({id}))}
      isValueEqual={(firstValue?: OptionValue, secondValue?: OptionValue) =>
        // There is probably a mistake in how we are rendering menu options that could be cleaned up, but
        // for some reason the Menu component is passing an object with a null id property for firstValue,
        // but a null value for secondValue when dealing with null items. The second part of the conditional
        // ensures we match that correctly.
        firstValue?.id === secondValue?.id || (firstValue?.id == null && secondValue == null)
      }
      scrollable={true}
    />
  );
}
